import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import ContactStudentModal from '../../components/ContactStudentModal';
import {
  UserIcon,
  AcademicCapIcon,
  TrophyIcon,
  StarIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

interface StudentProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  displayName?: string;
  avatar?: string;
  bio?: string;
  location?: string;
  skills?: string[];
  education?: {
    degree: string;
    institution: string;
    graduationYear: number;
  }[];
  experience?: {
    title: string;
    company: string;
    duration: string;
    description: string;
  }[];
  courseStats?: {
    enrolled: number;
    completed: number;
    certificationsEarned: number;
    averageScore: number;
  };
  skillRatings?: {
    skill: string;
    rating: number;
    verified: boolean;
  }[];
  overallRanking?: number;
  matchScore?: number;
}

interface FilterOptions {
  skills: string[];
  minExperience: string;
  location: string;
  minRanking: number;
  coursesCompleted: number;
  searchQuery: string;
}

const BrowseCandidatesPage: React.FC = () => {
  const [candidates, setCandidates] = useState<StudentProfile[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<StudentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentProfile | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({
    skills: [],
    minExperience: '',
    location: '',
    minRanking: 0,
    coursesCompleted: 0,
    searchQuery: ''
  });

  useEffect(() => {
    fetchCandidates();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [candidates, filters]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/employer/candidates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setCandidates(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch candidates');
      }
    } catch (error: any) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
      
      // Fallback to empty data
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const trackProfileView = async (studentId: string, source: string = 'search') => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/profile-views/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          profileOwnerId: studentId,
          source,
          pagesViewed: ['profile_overview']
        })
      });
    } catch (error) {
      console.error('Error tracking profile view:', error);
      // Don't show error to user as this is a background operation
    }
  };

  const handleViewProfile = (studentId: string) => {
    trackProfileView(studentId, 'search');
    // Navigate to student profile or open modal
    window.open(`/profile/student/${studentId}`, '_blank');
  };

  const handleContact = (student: StudentProfile) => {
    trackProfileView(student.id, 'contact_initiation');
    setSelectedStudent(student);
    setShowContactModal(true);
  };

  const handleMessageSent = () => {
    toast.success('Message sent successfully!');
    setShowContactModal(false);
    setSelectedStudent(null);
  };

  const applyFilters = () => {
    let filtered = [...candidates];

    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(candidate => 
        candidate.firstName.toLowerCase().includes(query) ||
        candidate.lastName.toLowerCase().includes(query) ||
        candidate.email.toLowerCase().includes(query) ||
        candidate.skills?.some(skill => skill.toLowerCase().includes(query)) ||
        candidate.bio?.toLowerCase().includes(query)
      );
    }

    // Skills filter
    if (filters.skills.length > 0) {
      filtered = filtered.filter(candidate =>
        candidate.skills && filters.skills.some(skill =>
          candidate.skills!.some(candidateSkill =>
            candidateSkill.toLowerCase().includes(skill.toLowerCase())
          )
        )
      );
    }

    // Location filter
    if (filters.location) {
      filtered = filtered.filter(candidate =>
        candidate.location?.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    // Minimum ranking filter
    if (filters.minRanking > 0) {
      filtered = filtered.filter(candidate =>
        candidate.overallRanking && candidate.overallRanking >= filters.minRanking
      );
    }

    // Courses completed filter
    if (filters.coursesCompleted > 0) {
      filtered = filtered.filter(candidate =>
        candidate.courseStats && candidate.courseStats.completed >= filters.coursesCompleted
      );
    }

    // Sort by match score (highest first)
    filtered.sort((a, b) => (b.matchScore || 0) - (a.matchScore || 0));

    setFilteredCandidates(filtered);
  };

  const handleFilterChange = (filterType: keyof FilterOptions, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      skills: [],
      minExperience: '',
      location: '',
      minRanking: 0,
      coursesCompleted: 0,
      searchQuery: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Browse Candidates</h1>
          <p className="mt-2 text-gray-600">
            Find the perfect candidates for your positions
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              {/* Search */}
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search candidates by name, skills, or bio..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  value={filters.searchQuery}
                  onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                />
              </div>
              
              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., San Francisco, Remote"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={filters.location}
                      onChange={(e) => handleFilterChange('location', e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Courses Completed
                    </label>
                    <input
                      type="number"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={filters.coursesCompleted}
                      onChange={(e) => handleFilterChange('coursesCompleted', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Ranking
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      value={filters.minRanking}
                      onChange={(e) => handleFilterChange('minRanking', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-600">
            Showing {filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Candidates Grid */}
        {filteredCandidates.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredCandidates.map((candidate) => (
              <div key={candidate.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        {candidate.avatar ? (
                          <img src={candidate.avatar} alt="" className="h-12 w-12 rounded-full" />
                        ) : (
                          <UserIcon className="h-6 w-6 text-indigo-600" />
                        )}
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {candidate.firstName} {candidate.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">{candidate.displayName || candidate.email}</p>
                      </div>
                    </div>
                    
                    {candidate.matchScore && (
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-600">
                          {candidate.matchScore}%
                        </div>
                        <div className="text-sm text-gray-500">Match</div>
                      </div>
                    )}
                  </div>

                  {/* Bio */}
                  {candidate.bio && (
                    <p className="text-gray-600 mb-4 line-clamp-2">{candidate.bio}</p>
                  )}

                  {/* Location */}
                  {candidate.location && (
                    <div className="flex items-center mb-3">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">{candidate.location}</span>
                    </div>
                  )}

                  {/* Course Stats */}
                  {candidate.courseStats && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center">
                        <AcademicCapIcon className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="text-sm text-gray-600">
                          {candidate.courseStats.completed} courses completed
                        </span>
                      </div>
                      <div className="flex items-center">
                        <TrophyIcon className="h-4 w-4 text-yellow-500 mr-2" />
                        <span className="text-sm text-gray-600">
                          {candidate.courseStats.certificationsEarned} certifications
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Overall Ranking */}
                  {candidate.overallRanking && (
                    <div className="flex items-center mb-4">
                      <StarIcon className="h-4 w-4 text-purple-500 mr-2" />
                      <span className="text-sm text-gray-600">
                        Ranking: #{candidate.overallRanking}
                      </span>
                    </div>
                  )}

                  {/* Skills */}
                  {candidate.skills && candidate.skills.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {candidate.skills.slice(0, 6).map((skill, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full"
                          >
                            {skill}
                          </span>
                        ))}
                        {candidate.skills.length > 6 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{candidate.skills.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex space-x-3 pt-4 border-t">
                    <button 
                      onClick={() => handleViewProfile(candidate.id)}
                      className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      View Profile
                    </button>
                    <button 
                      onClick={() => handleContact(candidate)}
                      className="flex-1 border border-indigo-600 text-indigo-600 px-4 py-2 rounded-lg hover:bg-indigo-50 transition-colors"
                    >
                      Contact
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No candidates found</h3>
            <p className="text-gray-600">
              Try adjusting your search criteria or filters to find more candidates.
            </p>
          </div>
        )}

        {/* Contact Candidate Modal */}
        {selectedStudent && (
          <ContactStudentModal
            isOpen={showContactModal}
            onClose={() => {
              setShowContactModal(false);
              setSelectedStudent(null);
            }}
            student={selectedStudent}
            onMessageSent={handleMessageSent}
          />
        )}
      </div>
    </div>
  );
};

export default BrowseCandidatesPage;